import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Space,
  Divider,
  Typography,
  Alert,
  Tag,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  UserOutlined,
  KeyOutlined,
  CopyOutlined,
  ReloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/auth';

const { Title, Text } = Typography;

interface UserProfile {
  id: number;
  username: string;
  email: string;
  user_type: string;
  status: string;
  client_id: number;
  parent_id?: number;
  api_key?: string;
  created_at: string;
  updated_at: string;
}

const Profile: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const { user, updateUser } = useAuthStore();
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // 加载用户资料
  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await authService.getProfile();
      console.log('用户资料响应:', response);
      setProfile(response.user);
      form.setFieldsValue(response.user);
    } catch (error) {
      console.error('加载用户资料失败:', error);
      message.error('加载用户资料失败');
    } finally {
      setLoading(false);
    }
  };

  // 更新用户资料
  const handleUpdateProfile = async (values: any) => {
    try {
      setLoading(true);
      await authService.updateProfile(values);
      message.success('个人资料更新成功');
      await loadProfile(); // 重新加载资料
    } catch (error) {
      console.error('更新个人资料失败:', error);
      message.error('更新个人资料失败');
    } finally {
      setLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async (values: any) => {
    try {
      setLoading(true);
      await authService.changePassword(values);
      message.success('密码修改成功');
      form.resetFields(['old_password', 'new_password', 'confirm_password']);
    } catch (error) {
      console.error('修改密码失败:', error);
      message.error('修改密码失败');
    } finally {
      setLoading(false);
    }
  };

  // 生成API密钥
  const handleGenerateAPIKey = async () => {
    try {
      setApiKeyLoading(true);
      const response = await authService.generateAPIKey();
      message.success('API密钥生成成功');
      await loadProfile(); // 重新加载资料
    } catch (error) {
      console.error('生成API密钥失败:', error);
      message.error('生成API密钥失败');
    } finally {
      setApiKeyLoading(false);
    }
  };

  // 复制API密钥
  const handleCopyAPIKey = async () => {
    if (profile?.api_key) {
      try {
        await navigator.clipboard.writeText(profile.api_key);
        message.success('API密钥已复制到剪贴板');
      } catch (error) {
        console.error('复制失败:', error);
        message.error('复制失败');
      }
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>个人资料</Title>
      
      <Row gutter={24}>
        {/* 基本信息 */}
        <Col span={16}>
          <Card title="基本信息" loading={loading}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleUpdateProfile}
              initialValues={profile}
            >
              <Form.Item
                label="用户名"
                name="username"
                rules={[{ required: true, message: '请输入用户名' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>

              <Form.Item
                label="邮箱"
                name="email"
                rules={[
                  { required: true, message: '请输入邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  更新资料
                </Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="修改密码" style={{ marginTop: 24 }}>
            <Form
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                label="当前密码"
                name="old_password"
                rules={[{ required: true, message: '请输入当前密码' }]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                label="新密码"
                name="new_password"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码至少6位' }
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                label="确认新密码"
                name="confirm_password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  修改密码
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* 账户信息 */}
        <Col span={8}>
          <Card title="账户信息">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>用户类型：</Text>
                <Tag color="blue">{profile?.user_type || '未知'}</Tag>
              </div>
              
              <div>
                <Text strong>账户状态：</Text>
                <Tag color={profile?.status === 'active' ? 'green' : 'red'}>
                  {profile?.status === 'active' ? '正常' : '禁用'}
                </Tag>
              </div>

              <div>
                <Text strong>客户ID：</Text>
                <Text>{profile?.client_id || 'N/A'}</Text>
              </div>

              <div>
                <Text strong>创建时间：</Text>
                <Text>{profile?.created_at ? new Date(profile.created_at).toLocaleString() : 'N/A'}</Text>
              </div>

              <div>
                <Text strong>最后更新：</Text>
                <Text>{profile?.updated_at ? new Date(profile.updated_at).toLocaleString() : 'N/A'}</Text>
              </div>
            </Space>
          </Card>

          {/* API密钥管理 */}
          <Card title="API密钥管理" style={{ marginTop: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {profile?.api_key ? (
                <>
                  <Alert
                    message="API密钥"
                    description="用于PHP中间层与Go Server通信的密钥"
                    type="info"
                    showIcon
                  />
                  
                  <div>
                    <Text strong>API密钥：</Text>
                    <div style={{ marginTop: 8 }}>
                      <Input
                        value={showApiKey ? profile.api_key : '••••••••••••••••'}
                        readOnly
                        addonAfter={
                          <Button
                            type="text"
                            icon={showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            onClick={() => setShowApiKey(!showApiKey)}
                          />
                        }
                      />
                    </div>
                  </div>

                  <Space>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={handleCopyAPIKey}
                    >
                      复制密钥
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      loading={apiKeyLoading}
                      onClick={handleGenerateAPIKey}
                    >
                      重新生成
                    </Button>
                  </Space>
                </>
              ) : (
                <>
                  <Alert
                    message="未设置API密钥"
                    description="您还没有API密钥，点击下方按钮生成一个"
                    type="warning"
                    showIcon
                  />
                  <Button
                    type="primary"
                    icon={<KeyOutlined />}
                    loading={apiKeyLoading}
                    onClick={handleGenerateAPIKey}
                    block
                  >
                    生成API密钥
                  </Button>
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
